// React imports
import { useState, useEffect, useRef, useCallback } from 'react';

// Other imports
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faEnvelope, faFileLines, faXmark } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import TransportationMethod from '../TransportationMethod.js';
import { AgentRunResult } from '../admin/RateAgentRun.js';
import { Pill } from './ElariomParts.js';
import ElariomReview from './ElariomReview.js';
import ElariomRunLog from './ElariomRunLog.js';
import ElariomReviewRates from './ElariomReviewRates.js';
import { CONVERTER_FORMATS } from './elariomData.js';

// Try the rate-parsing workflow on a dropped file: a PDF, Excel or CSV rate file, or an
// Outlook email saved as .eml (its rate attachments are taken out and its text becomes the
// supplier's notes). Nothing is written to the workspace; see the drops routes in admin.js.

const ACCEPT = '.pdf,.xlsx,.xls,.xlsm,.csv,.eml';
const MODE_LABEL = { air: 'Air', sea: 'Sea', land: 'Land' };

function formatDuration(ms) {
    if (!ms && ms !== 0) return '—';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

function formatSize(bytes) {
    if (!bytes) return '';
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function emailNotes(email) {
    if (!email) return '';
    return [email.subject ? `Subject: ${email.subject}` : null, email.text].filter(Boolean).join('\n\n');
}

function DropZone({ onFile, busy }) {
    const theme = useTheme();
    const input = useRef(null);
    const [ over, setOver ] = useState(false);

    return (
        <Box
            role='button'
            tabIndex={0}
            aria-label='Drop a rate file or an .eml email, or click to choose one'
            onClick={() => !busy && input.current?.click()}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !busy && input.current?.click()}
            onDragOver={e => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={e => {
                e.preventDefault();
                setOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file && !busy) onFile(file);
            }}
            sx={{
                border: `1.5px dashed ${over ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.4)}`,
                background: over ? alpha(theme.palette.primary.main, 0.06) : theme.palette.panel1,
                borderRadius: '10px', padding: '22px 16px', cursor: busy ? 'progress' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', textAlign: 'center',
                transition: 'background 120ms, border-color 120ms',
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px' },
            }}
        >
            <input ref={input} type='file' accept={ACCEPT} hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
            {busy ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: '22px', color: theme.palette.primary.main }} />}
            <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{busy ? 'Uploading…' : 'Drop a rate file or an email here'}</Typography>
                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                    PDF, Excel or CSV, or an Outlook email saved as .eml. Or click to choose a file.
                </Typography>
            </Box>
        </Box>
    );
}

function RunForm({ drop, org, accesstoken, onStarted, onClose }) {
    const theme = useTheme();
    // Pre-filled from, in order: the last run on this drop, what the file's own columns state
    // (mode, supplier, validity, FCL/LCL), then the email's sender, then today + 30 days.
    const last = drop.runs?.[0];
    const [ fileIndex, setFileIndex ] = useState(0);
    const hints = drop.files[fileIndex]?.hints || {};
    const [ mode, setMode ] = useState((last?.modeGiven !== false && last?.transportationMethod) || hints.transportationMethod || null);
    const [ shipmentType, setShipmentType ] = useState(last?.shipmentType || hints.shipmentType || null);
    const [ supplier, setSupplier ] = useState(last?.supplierGiven || hints.supplier || drop.email?.supplierGuess || drop.email?.fromName || '');
    const [ validFrom, setValidFrom ] = useState(last?.validFrom || hints.validFrom || dayjs().format('YYYY-MM-DD'));
    const [ validUntil, setValidUntil ] = useState(last?.validUntil || hints.validUntil || dayjs().add(30, 'day').format('YYYY-MM-DD'));
    const chooseFile = i => {
        setFileIndex(i);
        const h = drop.files[i]?.hints;
        if (!h) return;
        if (h.transportationMethod) setMode(h.transportationMethod);
        if (h.shipmentType) setShipmentType(h.shipmentType);
        if (h.supplier) setSupplier(h.supplier);
        if (h.validFrom) setValidFrom(h.validFrom);
        if (h.validUntil) setValidUntil(h.validUntil);
    };
    const [ notes, setNotes ] = useState(emailNotes(drop.email));
    // A file already in Monada's import format is checked by a script, not parsed by the agent.
    const [ useAgent, setUseAgent ] = useState(false);
    const scriptPath = !!hints.monadaCsv && !useAgent;
    // A file one of Monada's converters reads (metro trucking sheet, LCL tariff with CBM bands)
    // is converted without the agent, with the settings its admin page asks for.
    const converter = drop.files[fileIndex]?.converter;
    const converterPath = !!converter && !hints.monadaCsv && !useAgent;
    const [ conv, setConv ] = useState(() => ({
        portCode: converter?.portCode || '', portName: converter?.portName || '',
        // From the sheet where it says it; no made-up fuel figure (it used to start at 33%).
        fuelSurchargePct: converter?.fuelSurchargePct ?? '', fclBookingFee: 690, fclBookingFeeCurrency: 'AUD',
        supplier: converter?.supplier || '', contract: converter?.contract || '', validFrom: converter?.validFrom || '', validUntil: converter?.validUntil || '',
        // A one-way sheet starts with only its direction ticked.
        directions: converter?.direction ? [converter.direction] : ['import', 'export'],
    }));
    const setC = (k, v) => setConv(c => ({ ...c, [k]: v }));
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState(null);

    const start = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data: started } = await axios.post(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs?accesstoken=${accesstoken}`, scriptPath
                ? { fileIndex }
                : converterPath ? { fileIndex, converterOptions: conv }
                : { fileIndex, transportationMethod: mode || null, shipmentType: mode === 'sea' ? shipmentType : null, supplier, validFrom, validUntil, notes, useAgent });
            onStarted(started);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const label = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.palette.text.secondary };

    return (
        <Paper variant='outlined' sx={{ padding: '16px', borderColor: theme.palette.border1, background: theme.palette.panel1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', minWidth: 0 }}>
                    <FontAwesomeIcon icon={drop.email ? faEnvelope : faFileLines} style={{ marginTop: '3px', color: theme.palette.primary.main }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>{drop.email?.subject || drop.originalName}</Typography>
                        {drop.email && (
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, overflowWrap: 'anywhere' }}>
                                From {drop.email.fromName ? `${drop.email.fromName} <${drop.email.fromAddress}>` : drop.email.fromAddress || 'unknown sender'}
                                {drop.email.date ? ` · ${dayjs(drop.email.date).format('MMM D, YYYY HH:mm')}` : ''}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Button size='small' onClick={onClose} sx={{ minWidth: 0, color: theme.palette.text.secondary }} aria-label='Close'>
                    <FontAwesomeIcon icon={faXmark} />
                </Button>
            </Box>

            {drop.files.length === 0 ? (
                <Alert severity='warning' sx={{ fontSize: '13px' }}>
                    This email has no PDF, Excel or CSV attachment.{drop.ignored?.length ? ` Its attachments: ${drop.ignored.join(', ')}.` : ''}
                </Alert>
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Typography sx={label}>{drop.files.length > 1 ? 'Which file to parse' : 'File to parse'}</Typography>
                        {drop.files.map((f, i) => (
                            <Box key={f.url} onClick={() => chooseFile(i)} sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                {drop.files.length > 1 && <Radio size='small' checked={fileIndex === i} onChange={() => chooseFile(i)} inputProps={{ 'aria-label': f.name }} />}
                                <span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>
                                <Typography component='span' sx={{ fontSize: '12px', color: theme.palette.text.secondary, marginLeft: '6px' }}>{formatSize(f.size)}</Typography>
                            </Box>
                        ))}
                        {hints.fromColumns && !last && (
                            <Typography sx={{ fontSize: '12px', color: theme.palette.success.main }}>
                                Filled in from the file's own columns: {[hints.transportationMethod && 'mode', hints.shipmentType, hints.supplier && 'supplier', (hints.validFrom || hints.validUntil) && 'validity'].filter(Boolean).join(', ')}. Check before parsing.
                            </Typography>
                        )}
                        {!!drop.ignored?.length && (
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>Skipped, not a rate file: {drop.ignored.join(', ')}</Typography>
                        )}
                    </Box>

                    {converter && !hints.monadaCsv && (
                        <Alert severity={converterPath ? 'success' : 'info'} sx={{ fontSize: '13px' }}
                            action={<Button size='small' onClick={() => setUseAgent(v => !v)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>{converterPath ? 'Use the agent instead' : 'Use the converter instead'}</Button>}>
                            {converterPath
                                ? (converter.converter === 'cma-quote'
                                    ? `This file is ${CONVERTER_FORMATS['cma-quote'].name}, contract ${converter.contract || '?'}: ${converter.blockCount} load-port rates and ${converter.feederCount} feeder routes. A script builds every route exactly and checks it against the Summary tab. No agent, no tokens.`
                                    : converter.converter === 'air-template'
                                    ? `This file is ${CONVERTER_FORMATS['air-template'].name}. Every value is already in its own column, so a script converts it: each row becomes one rate, with the weight breaks built into one price formula. ${converter.rowCount} rows. No agent, no tokens, and it takes a second.`
                                    : `This file is ${CONVERTER_FORMATS[converter.converter]?.name}. Monada already converts this format (the same as ${CONVERTER_FORMATS[converter.converter]?.page}), so it doesn't need the agent: no tokens, and it takes a few seconds.`)
                                : 'The agent will read this file even though Monada has a converter for its format.'}
                        </Alert>
                    )}
                    {converterPath && converter.converter === 'metro-tariff' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                {converter.rowCount} suburbs in the sheet{converter.modes?.length ? `, priced for ${converter.modes.join(', ')}` : ''}. Check the settings below; they're filled in from the sheet where it says them.
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: '12px' }}>
                                <TextField id='elariom-metro-port' size='small' label={converter.air ? 'Airport code (IATA)' : 'Port code (UN/LOCODE)'} value={conv.portCode} onChange={e => setC('portCode', e.target.value.toUpperCase())}
                                    helperText={converter.air ? 'The airport the trucking runs from, e.g. MEL' : 'The port the trucking runs from, e.g. AUBNE'} />
                                <TextField id='elariom-metro-supplier' size='small' label='Supplier' value={conv.supplier} onChange={e => setC('supplier', e.target.value)} helperText='The trucking company' />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: '12px' }}>
                                <TextField id='elariom-metro-fuel' size='small' type='number' label='Fuel surcharge %' value={conv.fuelSurchargePct} onChange={e => setC('fuelSurchargePct', e.target.value)}
                                    required error={conv.fuelSurchargePct === ''}
                                    helperText={converter.fuelSurchargePct != null && String(conv.fuelSurchargePct) === String(converter.fuelSurchargePct) ? 'From the sheet' : conv.fuelSurchargePct === '' ? 'Not in the sheet, enter it' : ' '} />
                                {converter.modes?.includes('FCL') && <TextField id='elariom-metro-booking' size='small' type='number' label='FCL port booking fee' value={conv.fclBookingFee} onChange={e => setC('fclBookingFee', e.target.value)} />}
                                {converter.modes?.includes('FCL') && <TextField id='elariom-metro-booking-cur' size='small' label='Booking fee currency' value={conv.fclBookingFeeCurrency} onChange={e => setC('fclBookingFeeCurrency', e.target.value.toUpperCase())} />}
                                <TextField id='elariom-metro-from' size='small' type='date' label='Valid from' value={conv.validFrom} onChange={e => setC('validFrom', e.target.value)} InputLabelProps={{ shrink: true }} />
                                <TextField id='elariom-metro-until' size='small' type='date' label='Valid until' value={conv.validUntil} onChange={e => setC('validUntil', e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
                                {[['import', `From the ${converter.air ? 'airport' : 'port'} to the door`], ['export', `From the door to the ${converter.air ? 'airport' : 'port'}`]].map(([d, label]) => (
                                    <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type='checkbox' checked={conv.directions.includes(d)} onChange={() => setC('directions', conv.directions.includes(d) ? conv.directions.filter(x => x !== d) : [...conv.directions, d])} />
                                        {label}
                                    </label>
                                ))}
                            </Box>
                            {converter.direction && (
                                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                                    Only "{converter.direction === 'import' ? `from the ${converter.air ? 'airport' : 'port'} to the door` : `from the door to the ${converter.air ? 'airport' : 'port'}`}" is ticked: {converter.directionReason}. Tick the other one too only if the customer said these prices work both ways.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {converterPath && converter.converter === 'cma-quote' && (
                        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <TextField id='elariom-cma-supplier' size='small' label='Supplier' value={conv.supplier} onChange={e => setC('supplier', e.target.value)} helperText='The name the rates are listed under' />
                            <TextField id='elariom-cma-contract' size='small' label='Contract No.' value={conv.contract} onChange={e => setC('contract', e.target.value.toUpperCase())} helperText='Shown on every rate card' />
                        </Box>
                    )}
                    {converterPath && converter.converter === 'lcl-tiers' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                {converter.routeCount} routes found{converter.sheets?.length ? ` on the sheets ${converter.sheets.join(', ')}` : ''}. Each becomes a rate with its full CBM band ladder.
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '12px', maxWidth: '480px' }}>
                                <TextField id='elariom-lcl-from' size='small' type='date' label='Valid from' value={conv.validFrom} onChange={e => setC('validFrom', e.target.value)} InputLabelProps={{ shrink: true }} />
                                <TextField id='elariom-lcl-until' size='small' type='date' label='Valid until' value={conv.validUntil} onChange={e => setC('validUntil', e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Box>
                        </Box>
                    )}
                    {hints.monadaCsv && (
                        <Alert severity={scriptPath ? 'success' : 'info'} sx={{ fontSize: '13px' }}
                            action={<Button size='small' onClick={() => setUseAgent(v => !v)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>{scriptPath ? 'Use the agent instead' : 'Check with a script instead'}</Button>}>
                            {scriptPath
                                ? 'This file is already in Monada\'s import format. A script checks it the way the importer will read it, fixes what is mechanical, and sends it to review. No agent, no tokens.'
                                : 'The agent will re-read this file even though it is already in Monada\'s import format.'}
                        </Alert>
                    )}
                    {!scriptPath && !converterPath && (<>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                            <Typography sx={label}>Mode</Typography>
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                                {mode ? 'Given: the agent keeps it.' : 'Not given: the agent decides it from the file and the email.'}
                            </Typography>
                            {mode && (
                                <Button size='small' onClick={() => { setMode(null); setShipmentType(null); }} sx={{ textTransform: 'none', fontSize: '12px', padding: '0 6px', minWidth: 0 }}>
                                    Let the agent decide
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ maxWidth: '420px' }}>
                            <TransportationMethod transportationMethod={mode} setTransportationMethod={setMode} />
                        </Box>
                        {mode === 'sea' && (
                            <ToggleButtonGroup exclusive size='small' value={shipmentType} onChange={(e, v) => v && setShipmentType(v)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '12px', padding: '3px 14px' } }}>
                                <ToggleButton value='FCL'>FCL (containers)</ToggleButton>
                                <ToggleButton value='LCL'>LCL (by cbm / ton)</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: '12px' }}>
                        <TextField id='elariom-drop-supplier' size='small' label='Supplier' value={supplier} onChange={e => setSupplier(e.target.value)} helperText='Optional. Leave empty and the agent names it from the file.' />
                        <TextField id='elariom-drop-from' size='small' type='date' label='Valid from' value={validFrom} onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField id='elariom-drop-until' size='small' type='date' label='Valid until' value={validUntil} onChange={e => setValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Box>

                    <TextField
                        id='elariom-drop-notes'
                        size='small'
                        label="Supplier's notes"
                        multiline
                        minRows={2}
                        maxRows={8}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        helperText={drop.email ? 'Taken from the email. The agent reads these alongside the file.' : 'Optional. Anything the supplier said about this file, e.g. in the email that came with it.'}
                    />

                    </>)}

                    {error && <Alert severity='error' sx={{ fontSize: '13px' }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Button variant='contained' color='monada-button' disabled={busy || (converterPath && converter.converter === 'metro-tariff' && conv.fuelSurchargePct === '')} onClick={start} sx={{ textTransform: 'none' }}>
                            {busy ? (scriptPath ? 'Checking…' : converterPath ? 'Converting…' : 'Starting…') : scriptPath ? 'Check & review' : converterPath ? 'Convert & review' : 'Parse'}
                        </Button>
                        <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                            {scriptPath || converterPath ? 'Takes a few seconds. Nothing is saved to the workspace until you publish.' : 'Takes a few minutes. Nothing is saved to the workspace until you publish.'}
                        </Typography>
                    </Box>
                </>
            )}
        </Paper>
    );
}

function RunDialog({ org, accesstoken, drop, runSummary, onClose, onPublished }) {
    const [ run, setRun ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ reviewing, setReviewing ] = useState(false);
    const published = runSummary.publication?.status === 'published';

    useEffect(() => {
        if (runSummary.status !== 'done') return;
        axios.get(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${runSummary.id}?accesstoken=${accesstoken}`)
            .then(({ data }) => setRun(data))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [org.id, drop.id, runSummary, accesstoken]);

    return (
        <Dialog open onClose={onClose} maxWidth='xl' fullWidth>
            <DialogTitle>
                {drop.email?.subject || drop.originalName}
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {runSummary.fileName} · {MODE_LABEL[runSummary.transportationMethod]}{runSummary.shipmentType ? ` ${runSummary.shipmentType}` : ''} · {dayjs(runSummary.at).format('MMM D, YYYY HH:mm')}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {runSummary.status === 'failed' && <Alert severity='error'>The run failed: {runSummary.error}</Alert>}
                {error && <Alert severity='error'>{error}</Alert>}
                {runSummary.status === 'done' && !run && !error && <CircularProgress size={24} />}
                {run && <AgentRunResult run={run} gapsNote='A test run: these gaps are not added to the rule book queue.' />}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
                {run && (
                    <Button variant='contained' color='monada-button' onClick={() => setReviewing(true)} sx={{ textTransform: 'none' }}>
                        {published ? 'Open published rates' : 'Review & publish'}
                    </Button>
                )}
            </DialogActions>
            {reviewing && run && run.kind === 'converter' && (
                <ElariomReviewRates
                    org={org}
                    accesstoken={accesstoken}
                    drop={drop}
                    runSummary={runSummary}
                    run={run}
                    onClose={() => setReviewing(false)}
                    onPublished={publication => { setReviewing(false); onPublished(publication); }}
                />
            )}
            {reviewing && run && run.kind !== 'converter' && (
                <ElariomReview
                    org={org}
                    accesstoken={accesstoken}
                    drop={drop}
                    runSummary={runSummary}
                    run={run}
                    onClose={() => setReviewing(false)}
                    onPublished={publication => { setReviewing(false); onPublished(publication); }}
                />
            )}
        </Dialog>
    );
}

// startFile: a file picked elsewhere (the Rates → Import page) to upload straight away.
function ElariomTryIt({ org, accesstoken, startFile = null }) {
    const theme = useTheme();
    const [ drops, setDrops ] = useState(null);
    const [ uploading, setUploading ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ active, setActive ] = useState(null);
    const [ open, setOpen ] = useState(null);
    const [ logOf, setLogOf ] = useState(null);

    const load = useCallback(() => axios.get(`/api/admin/elariom/${org.id}/drops?accesstoken=${accesstoken}`)
        .then(({ data }) => setDrops(data))
        .catch(e => setError(e.response?.data?.detail || e.message)), [org.id, accesstoken]);

    useEffect(() => { load(); }, [load]);

    // While a run is going, check every 5 seconds for its result.
    const running = (drops || []).some(d => (d.runs || []).some(r => r.status === 'running'));
    useEffect(() => {
        if (!running) return undefined;
        const timer = setInterval(load, 5000);
        return () => clearInterval(timer);
    }, [running, load]);

    const onFile = async file => {
        setUploading(true);
        setError(null);
        try {
            const form = new FormData();
            form.append('file', file, file.name);
            const { data } = await axios.post(`/api/admin/elariom/${org.id}/drops?accesstoken=${accesstoken}`, form);
            setActive(data);
            load();
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setUploading(false);
        }
    };

    const started = useRef(false);
    useEffect(() => {
        if (!startFile || started.current) return;
        started.current = true;
        onFile(startFile);
    }, [startFile]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = _.flatMap(drops || [], d => ((d.runs || []).length ? d.runs.map(r => ({ drop: d, run: r })) : [{ drop: d, run: null }]));
    const head = { fontSize: '11px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
    const cell = { fontSize: '12px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {error && <Alert severity='error' sx={{ fontSize: '13px' }} onClose={() => setError(null)}>{error}</Alert>}
            {active
                ? <RunForm key={active.id} drop={active} org={org} accesstoken={accesstoken} onStarted={run => { const d = active; setActive(null); load(); if (run?.kind === 'script' || run?.kind === 'converter') setOpen({ drop: d, run }); }} onClose={() => setActive(null)} />
                : <DropZone onFile={onFile} busy={uploading} />}

            {!!rows.length && (
                <Box sx={{ overflowX: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                {['When', 'Dropped', 'File', 'Mode', 'Status', 'Kind of file', 'Rates', 'Held back', 'Gaps', 'Time', ''].map((h, i) => <TableCell key={i} sx={head}>{h}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(({ drop, run }) => (
                                <TableRow
                                    key={`${drop.id}-${run?.id || 'none'}`}
                                    hover
                                    onClick={() => (run?.status === 'running' ? setLogOf({ drop, run }) : run && run.status === 'done' ? setOpen({ drop, run }) : setActive(drop))}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={cell}>{dayjs(run?.at || drop.at).format('MMM D, HH:mm')}</TableCell>
                                    <TableCell sx={{ ...cell, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={drop.email?.subject || drop.originalName}>
                                        <FontAwesomeIcon icon={drop.email ? faEnvelope : faFileLines} style={{ fontSize: '11px', marginRight: '6px', color: theme.palette.text.secondary }} />
                                        {drop.email?.subject || drop.originalName}
                                    </TableCell>
                                    <TableCell sx={{ ...cell, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={run?.fileName || ''}>{run?.fileName || '—'}</TableCell>
                                    <TableCell sx={cell} title={run?.modeGiven === false ? 'Decided by the agent from the file' : ''}>{run?.transportationMethod ? `${MODE_LABEL[run.transportationMethod]}${run.shipmentType ? ` ${run.shipmentType}` : ''}${run.modeGiven === false ? ' · by agent' : ''}` : run?.status === 'running' ? 'Agent decides' : '—'}</TableCell>
                                    <TableCell sx={cell}>
                                        {!run && <Pill color={theme.palette.text.secondary} dashed>Not parsed yet</Pill>}
                                        {run?.status === 'running' && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><CircularProgress size={11} /><span>Parsing…</span></Box>}
                                        {run?.status === 'done' && run.publication?.status === 'published' && <Pill color={theme.palette.primary.main}>Published · {run.publication.rateCount}</Pill>}
                                        {run?.status === 'done' && run.publication?.status === 'unpublished' && <Pill color={theme.palette.text.secondary}>Unpublished</Pill>}
                                        {run?.status === 'done' && !run.publication && <Pill color={theme.palette.success.main}>{run.kind === 'script' ? 'Checked · to review' : run.kind === 'converter' ? 'Converted · to review' : run.readWith === 'saved recipe' ? 'Read with saved recipe · to review' : run.readWith === 'new recipe' ? 'Read with a recipe · to review' : 'Parsed · to review'}</Pill>}
                                        {run?.status === 'failed' && <Pill color={theme.palette.error.main}>Failed</Pill>}
                                        {run?.status === 'stopped' && <Pill color={theme.palette.text.secondary}>Stopped</Pill>}
                                    </TableCell>
                                    <TableCell sx={cell}>{run?.classification?.category || '—'}</TableCell>
                                    <TableCell sx={cell}>{run?.status === 'done' ? run.rateCount : '—'}</TableCell>
                                    <TableCell sx={cell}>{run?.status === 'done' ? run.heldBackCount : '—'}</TableCell>
                                    <TableCell sx={cell}>{run?.status === 'done' ? run.gapCount : '—'}</TableCell>
                                    <TableCell sx={cell}>{run?.status === 'done' ? formatDuration(run.durationMs) : '—'}</TableCell>
                                    <TableCell sx={cell} onClick={e => e.stopPropagation()}>
                                        {run && !['script', 'converter'].includes(run.kind) && (
                                            <Button size='small' sx={{ textTransform: 'none', fontSize: '12px', padding: '0 6px', minWidth: 0 }} onClick={() => setLogOf({ drop, run })}>
                                                {run.status === 'running' ? 'Log · Stop' : 'Log'}
                                            </Button>
                                        )}
                                        <Button size='small' sx={{ textTransform: 'none', fontSize: '12px', padding: '0 6px', minWidth: 0 }} onClick={() => setActive(drop)}>
                                            {run ? 'Parse again' : 'Parse'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {logOf && (
                <ElariomRunLog
                    org={org}
                    accesstoken={accesstoken}
                    drop={logOf.drop}
                    run={(drops || []).find(d => d.id === logOf.drop.id)?.runs?.find(r => r.id === logOf.run.id) || logOf.run}
                    onClose={() => setLogOf(null)}
                    onStopped={load}
                />
            )}
            {open && (
                <RunDialog
                    org={org}
                    accesstoken={accesstoken}
                    drop={open.drop}
                    runSummary={open.run}
                    onClose={() => setOpen(null)}
                    onPublished={publication => { setOpen(o => (o ? { ...o, run: { ...o.run, publication } } : o)); load(); }}
                />
            )}
        </Box>
    );
}

export { ACCEPT as RATE_FILE_TYPES };
export default ElariomTryIt;
